import { useEffect, useRef } from "react";

const STAR_COUNT = 120;

interface Star {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  o: number;
  oDir: number;
}

interface GlowPulse {
  x: number;
  y: number;
  maxR: number;
  r: number;
  life: number;
  maxLife: number;
}

const Particles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const glowsRef = useRef<GlowPulse[]>([]);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    starsRef.current = Array.from({ length: STAR_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.2 + 0.3,
      vx: (Math.random() - 0.5) * 0.12,
      vy: (Math.random() - 0.5) * 0.08,
      o: Math.random() * 0.35 + 0.05,
      oDir: Math.random() > 0.5 ? 1 : -1,
    }));

    let raf: number;
    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      timeRef.current += 1;
      const t = timeRef.current;

      // Slow gradient background shift
      const hueShift = Math.sin(t * 0.001) * 5;
      const yShift = 0.45 + Math.sin(t * 0.0008) * 0.05;
      const grad = ctx.createRadialGradient(
        w * 0.5, h * yShift, 0,
        w * 0.5, h * yShift, w * 0.6
      );
      grad.addColorStop(0, `hsla(${155 + hueShift}, 80%, 50%, 0.012)`);
      grad.addColorStop(0.5, `hsla(${230 + hueShift}, 25%, 10%, 0.008)`);
      grad.addColorStop(1, "transparent");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Stars
      for (const s of starsRef.current) {
        s.x += s.vx;
        s.y += s.vy;
        s.o += s.oDir * 0.002;
        if (s.o > 0.5 || s.o < 0.03) s.oDir *= -1;
        if (s.x < 0) s.x = w;
        if (s.x > w) s.x = 0;
        if (s.y < 0) s.y = h;
        if (s.y > h) s.y = 0;

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 210, 230, ${s.o})`;
        ctx.fill();
      }

      // Occasional green glow pulses
      if (Math.random() < 0.006) {
        glowsRef.current.push({
          x: Math.random() * w,
          y: Math.random() * h,
          maxR: 80 + Math.random() * 120,
          r: 0,
          life: 0,
          maxLife: 180 + Math.random() * 120,
        });
      }

      // Keep max 3 glows
      if (glowsRef.current.length > 3) glowsRef.current.shift();

      for (let i = glowsRef.current.length - 1; i >= 0; i--) {
        const g = glowsRef.current[i];
        g.life += 1;
        const progress = g.life / g.maxLife;
        g.r = g.maxR * Math.sin(progress * Math.PI);
        const alpha = 0.04 * Math.sin(progress * Math.PI);

        if (g.life >= g.maxLife) {
          glowsRef.current.splice(i, 1);
          continue;
        }

        const gGrad = ctx.createRadialGradient(g.x, g.y, 0, g.x, g.y, g.r);
        gGrad.addColorStop(0, `rgba(0, 255, 136, ${alpha})`);
        gGrad.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
        ctx.fillStyle = gGrad;
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ width: "100%", height: "100%" }}
    />
  );
};

export default Particles;
